import React, { Component } from 'react';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';

import css from './SearchMapPriceLabel.module.css';

/**
 * SearchMapPriceLabel component
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.listing} props.listing - The listing
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {Object} props.config - The configuration
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = this.props.listing ? ensureListing(this.props.listing) : null;
    const nextListing = nextProps.listing ? ensureListing(nextProps.listing) : null;
    const isSameListing =
      currentListing && nextListing ? currentListing.id.uuid === nextListing.id.uuid : true;
    const hasSamePrice =
      currentListing && nextListing
        ? currentListing.attributes.price === nextListing.attributes.price
        : true;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    const hasSameLabel = this.props.customLabel === nextProps.customLabel;
    const hasSameAriaLabel = this.props.customAriaLabel === nextProps.customAriaLabel;

    return !(
      isSameListing &&
      hasSamePrice &&
      hasSameActiveStatus &&
      hasSameRefreshToken &&
      hasSameLabel &&
      hasSameAriaLabel
    );
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
      customLabel,
      customAriaLabel,
      onClick,
    } = this.props;
    const currentListing = listing ? ensureListing(listing) : null;
    const { price, publicData } = currentListing?.attributes || {};

    // Create formatted price if currency is known or alternatively show just the unknown currency.
    const formattedPrice =
      price && price.currency === config.currency
        ? formatMoney(intl, price)
        : price?.currency
        ? price.currency
        : null;

    const priceValue = formattedPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.price' }, { priceValue: formattedPrice })
      : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = customLabel
      ? customLabel
      : hasMultiplePriceVariants
      ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue })
      : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue });

    const classes = classNames(rootClassName || css.root, className);
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !formattedPrice,
    });
    const caretClasses = classNames(css.caret, { [css.caretActive]: isActive });

    const ariaLabel = customAriaLabel
      ? customAriaLabel
      : priceValue
      ? priceMessage
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() =>
          onClick ? onClick() : currentListing ? onListingClicked(currentListing) : null
        }
        aria-label={ariaLabel}
      >
        <div className={css.caretShadow} />
        <div className={priceLabelClasses}>{priceMessage}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

export default injectIntl(SearchMapPriceLabel);
